// Pure rent-vs-buy comparison math. No DOM, no dependencies.

export interface RentVsBuyInputs {
  homePrice: string;
  downPayment: string;
  mortgageRatePct: string;
  termYears: string;
  propertyTaxPct: string;
  maintenancePct: string;
  homeAppreciationPct: string;
  sellingCostPct: string;
  monthlyRent: string;
  rentGrowthPct: string;
  investmentReturnPct: string;
  yearsToStay: string;
}

export interface RentVsBuyYear {
  year: number;
  buyCumulative: number;
  rentCumulative: number;
  homeValue: number;
  loanBalance: number;
  equity: number;
}

export interface RentVsBuyResult {
  ok: boolean;
  message?: string;
  monthlyMortgage?: number;
  totalBuy?: number;
  totalRent?: number;
  breakEvenYear?: number | null;
  cheaper?: 'buy' | 'rent' | 'equal';
  difference?: number;
  years?: RentVsBuyYear[];
}

function toNumber(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') {
    return NaN;
  }
  return Number(cleaned);
}

/** Standard fully-amortising monthly payment. */
export function monthlyMortgagePayment(
  principal: number,
  annualRatePct: number,
  termYears: number,
): number {
  const months = termYears * 12;
  if (months <= 0) {
    return 0;
  }
  const monthlyRate = annualRatePct / 100 / 12;
  if (monthlyRate === 0) {
    return principal / months;
  }
  const factor = Math.pow(1 + monthlyRate, months);
  return (principal * monthlyRate * factor) / (factor - 1);
}

export function computeRentVsBuy(inputs: RentVsBuyInputs): RentVsBuyResult {
  const homePrice = toNumber(inputs.homePrice);
  const downPayment = toNumber(inputs.downPayment);
  const ratePct = toNumber(inputs.mortgageRatePct);
  const termYears = toNumber(inputs.termYears);
  const taxPct = toNumber(inputs.propertyTaxPct);
  const maintPct = toNumber(inputs.maintenancePct);
  const appreciationPct = toNumber(inputs.homeAppreciationPct);
  const sellingCostPct = toNumber(inputs.sellingCostPct);
  const monthlyRent = toNumber(inputs.monthlyRent);
  const rentGrowthPct = toNumber(inputs.rentGrowthPct);
  const investReturnPct = toNumber(inputs.investmentReturnPct);
  const yearsToStay = toNumber(inputs.yearsToStay);

  const required = [
    homePrice,
    downPayment,
    ratePct,
    termYears,
    taxPct,
    maintPct,
    appreciationPct,
    sellingCostPct,
    monthlyRent,
    rentGrowthPct,
    investReturnPct,
    yearsToStay,
  ];
  if (required.some((value) => Number.isNaN(value))) {
    return { ok: false, message: 'Fill in every field with a valid number.' };
  }
  if (homePrice <= 0) {
    return { ok: false, message: 'Home price must be greater than zero.' };
  }
  if (downPayment < 0 || downPayment > homePrice) {
    return { ok: false, message: 'Down payment must be between 0 and the home price.' };
  }
  if (termYears <= 0) {
    return { ok: false, message: 'Mortgage term must be greater than zero.' };
  }
  if (yearsToStay <= 0 || yearsToStay > 60) {
    return { ok: false, message: 'Years to stay must be between 1 and 60.' };
  }

  const loanPrincipal = homePrice - downPayment;
  const monthlyRate = ratePct / 100 / 12;
  const payment = monthlyMortgagePayment(loanPrincipal, ratePct, termYears);
  const totalMonths = termYears * 12;
  const stayYears = Math.floor(yearsToStay);

  const years: RentVsBuyYear[] = [];

  // Buying: down payment is spent up front and is not recoverable as "cost"
  // until we net out sale equity, so start the cumulative at the down payment.
  let buyCumulative = downPayment;
  let loanBalance = loanPrincipal;
  let homeValue = homePrice;

  // Renting: the buyer's down payment would have been invested instead.
  let investmentValue = downPayment;
  let rentCumulative = 0;
  let currentAnnualRent = monthlyRent * 12;

  let breakEvenYear: number | null = null;

  for (let year = 1; year <= stayYears; year += 1) {
    // --- Buying costs for this year ---
    let mortgagePaidThisYear = 0;
    for (let m = 0; m < 12; m += 1) {
      const monthIndex = (year - 1) * 12 + m;
      if (monthIndex < totalMonths && loanBalance > 0) {
        const interest = loanBalance * monthlyRate;
        let principalPart = payment - interest;
        if (principalPart > loanBalance) {
          principalPart = loanBalance;
        }
        mortgagePaidThisYear += principalPart + interest;
        loanBalance -= principalPart;
        if (loanBalance < 0) {
          loanBalance = 0;
        }
      }
    }
    const taxThisYear = homeValue * (taxPct / 100);
    const maintThisYear = homeValue * (maintPct / 100);
    buyCumulative += mortgagePaidThisYear + taxThisYear + maintThisYear;

    // Home appreciates at the end of the year.
    homeValue *= 1 + appreciationPct / 100;

    // --- Renting costs for this year ---
    rentCumulative += currentAnnualRent;
    currentAnnualRent *= 1 + rentGrowthPct / 100;
    // The invested down payment (plus any growth) compounds.
    investmentValue *= 1 + investReturnPct / 100;

    // Net position at the end of this year if the buyer sold now.
    const saleProceeds = homeValue - loanBalance - homeValue * (sellingCostPct / 100);
    const equity = homeValue - loanBalance;
    const buyNet = buyCumulative - saleProceeds;
    // The renter's investment gain (above the original principal) offsets rent.
    const rentNet = rentCumulative - (investmentValue - downPayment);

    years.push({
      year,
      buyCumulative: buyNet,
      rentCumulative: rentNet,
      homeValue,
      loanBalance,
      equity,
    });

    if (breakEvenYear === null && buyNet <= rentNet) {
      breakEvenYear = year;
    }
  }

  const last = years[years.length - 1];
  const totalBuy = last.buyCumulative;
  const totalRent = last.rentCumulative;
  const difference = Math.abs(totalBuy - totalRent);
  let cheaper: 'buy' | 'rent' | 'equal' = 'equal';
  if (totalBuy < totalRent - 1) {
    cheaper = 'buy';
  } else if (totalRent < totalBuy - 1) {
    cheaper = 'rent';
  }

  return {
    ok: true,
    monthlyMortgage: payment,
    totalBuy,
    totalRent,
    breakEvenYear,
    cheaper,
    difference,
    years,
  };
}
